import { AlertCircle, ArrowRight, Mail } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { appConfig } from "@/config/appConfig";
import { useAuthStore } from "@/store/auth";
import { useOrganizationStore } from "@/store/organization";
import {
  BrandingSidebar,
  OnboardingLayout,
} from "./components/onboarding-layout";

/**
 * Blocks dashboard access while activation is pending. Contacting sales opens
 * a prefilled mail client and marks the request sent; sign-out returns to the
 * application origin.
 *
 * @returns The pending-approval onboarding view with contact and sign-out actions.
 */
export const PendingApprovalView = () => {
  const signOut = useAuthStore((s) => s.signOut);
  const organization = useOrganizationStore((s) => s.organization);
  const [contactFormSubmitted, setContactFormSubmitted] =
    useState<boolean>(false);

  const {
    company: { salesEmail },
    features: {
      organization: { activationEmailSubject },
    },
    ui: { accountAccess },
  } = appConfig;

  const handleContactSales = () => {
    const subject = encodeURIComponent(activationEmailSubject);
    const body = encodeURIComponent(
      `Hello,\n\nI would like to request activation for my organization (${organization?.name || "Name not available"}).\n\nThank you.`,
    );

    window.open(
      `mailto:${salesEmail}?subject=${subject}&body=${body}`,
      "_blank",
    );
    setContactFormSubmitted(true);
  };

  const handleSignOut = () => {
    signOut({ redirectUrl: window.location.origin });
  };

  return (
    <OnboardingLayout
      sidebar={
        <BrandingSidebar
          description={accountAccess.pendingDescription}
          featuresHeading="Once activated, you'll have access to:"
        />
      }
      footer={
        <div className="mt-2 text-center">
          <button
            onClick={handleSignOut}
            className="text-sm transition-colors text-muted-foreground hover:text-primary hover:underline"
          >
            Sign out
          </button>
        </div>
      }
    >
      <div className="flex items-start space-x-4">
        <AlertCircle className="mt-1 w-6 h-6 text-amber-500" />
        <div>
          <h2 className="text-3xl font-bold tracking-tight">
            {accountAccess.pendingTitle}
          </h2>
          <p className="mt-3 text-muted-foreground">
            Your organization{" "}
            <span className="font-medium">{organization?.name}</span> has been
            created but requires activation before you can access the API
            Dashboard.
          </p>
        </div>
      </div>

      <div className="p-6 space-y-4 rounded-lg border">
        <h3 className="text-xl font-semibold">Next step: Contact Sales</h3>
        <p>
          To activate your account, you must contact our sales team. This is a
          required step before you can access the API Dashboard.
        </p>

        {contactFormSubmitted ? (
          <div className="p-4 text-green-700 bg-green-50 rounded-md">
            {accountAccess.thankYouMessage}
          </div>
        ) : (
          <Button
            onClick={handleContactSales}
            className="mt-4 w-full h-12 text-base group"
            size="lg"
          >
            <Mail className="mr-2 w-4 h-4" />
            Contact Sales to Activate
            <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
          </Button>
        )}
      </div>
    </OnboardingLayout>
  );
};
